// Reclassify confirmation dialog, design §5: the tools menu's "Reclassify
// transactions…" opens this dialog rather than posting immediately. Once
// the POST is acknowledged (fresh or deduped), the returned job id is
// registered with the shared job registry (jobRegistry.ts) and progress
// renders in JobStatusRow / the page-level status row -- this component
// owns only the confirm -> enqueue step of §5's table, never the
// queued/running/done rendering (that is JobStatusRow's job).
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@moneyskyline/ui';
import { useRecategorize } from '@/api/hooks/useRecategorize';
import { registerJob } from './tools/jobRegistry';
import { emitReclassifyStart, newInteractionId, viewportBandFromShellBand } from './telemetry';
import { viewportBandForWidth } from '@/app/navigation/viewportBand';

type ConfirmState = 'idle' | 'pending' | 'rejected' | 'unknown' | { kind: 'started'; deduped: boolean; jobId: string };

export function ReclassifyConfirmDialog({
  open,
  onOpenChange,
  onViewProgress,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onViewProgress?: () => void;
}) {
  const [state, setState] = useState<ConfirmState>('idle');
  const recategorize = useRecategorize();

  function start() {
    setState('pending');
    recategorize.mutate(
      { scope: 'all' },
      {
        onSuccess: (data) => {
          registerJob(data.job_id, 'tools');
          emitReclassifyStart(
            {
              surface: 'tools',
              viewport_band: viewportBandFromShellBand(
                typeof window === 'undefined' ? 'desktop' : viewportBandForWidth(window.innerWidth),
              ),
              input_method: 'unknown',
            },
            newInteractionId(),
            { job_id: data.job_id, scope: 'all', origin: 'tools', deduped: data.deduped },
          );
          setState({ kind: 'started', deduped: data.deduped, jobId: data.job_id });
        },
        onError: (error: unknown) => {
          // No response reached the caller -- the enqueue's outcome is genuinely unknown; a
          // definitive HTTP error response is a real rejection (§5 POST rejected vs unknown).
          const hasResponse = Boolean((error as { response?: unknown } | undefined)?.response);
          setState(hasResponse ? 'rejected' : 'unknown');
        },
      },
    );
  }

  function close() {
    setState('idle');
    onOpenChange(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          // "user may close dialog" during POST pending (§5) -- closing never cancels the request.
          setState('idle');
        }
        onOpenChange(next);
      }}
    >
      <DialogContent data-testid="reclassify-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Reclassify transactions</DialogTitle>
          <DialogDescription>
            Review categories across all accounts and dates. Categories you set yourself stay as they are. This may
            update transactions outside this view.
          </DialogDescription>
        </DialogHeader>

        {state === 'rejected' && (
          <p role="alert" data-testid="reclassify-rejected">
            Couldn&rsquo;t start reclassification.
          </p>
        )}
        {state === 'unknown' && (
          <p role="alert" data-testid="reclassify-unknown">
            We couldn&rsquo;t confirm whether reclassification started. Check or start reclassification.
          </p>
        )}
        {typeof state === 'object' && state.kind === 'started' && (
          <p role="status" data-testid={state.deduped ? 'reclassify-deduped' : 'reclassify-started'}>
            {state.deduped ? 'A review is already in progress.' : 'Reclassification queued'}
          </p>
        )}

        <DialogFooter>
          {typeof state === 'object' && state.kind === 'started' ? (
            <>
              <Button type="button" variant="outline" onClick={close}>
                Close
              </Button>
              <Button
                type="button"
                onClick={() => {
                  close();
                  onViewProgress?.();
                }}
              >
                View progress
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={close} disabled={state === 'pending'}>
                Cancel
              </Button>
              <Button type="button" onClick={start} disabled={state === 'pending'}>
                {state === 'pending' ? 'Starting reclassification…' : 'Start reclassifying'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReclassifyConfirmDialog;
